/**
 * Action Bar Component
 * Bottom action bar with approve/reject buttons and navigation
 */

import React, { useState, useCallback } from 'react';
import KeyboardHelp from './KeyboardHelp';

const ActionBar = ({
  // Item info
  currentIndex = 0,
  totalItems = 0,
  item = null,

  // Status
  isAssigned = false,
  isAssignedToMe = false,
  canApprove = false,
  isSaving = false,
  isDirty = false,

  // Actions
  onAssign,
  onApprove,
  onApproveAndNext,
  onReject,
  onSkip,
  onDelete,
  onReset,

  // Notes
  notes = '',
  onNotesChange,

  className = ''
}) => {
  const [showKeyboardHelp, setShowKeyboardHelp] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Format age
  const formatAge = (dateStr) => {
    if (!dateStr) return '';
    const created = new Date(dateStr);
    const now = new Date();
    const diffMs = now - created;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    if (diffHours > 24) {
      return `${Math.floor(diffHours / 24)}d ${diffHours % 24}h`;
    }
    if (diffHours > 0) {
      return `${diffHours}h ${diffMins}m`;
    }
    return `${diffMins}m`;
  };

  // Priority badge
  const getPriorityBadge = (priority) => {
    const colors = {
      urgent: 'bg-red-100 text-red-800 border-red-200',
      high: 'bg-orange-100 text-orange-800 border-orange-200',
      medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      low: 'bg-green-100 text-green-800 border-green-200'
    };
    return colors[priority] || colors.medium;
  };

  return (
    <>
      <div className={`bg-white border-t border-gray-200 ${className}`}>
        {/* Notes section (collapsible) */}
        {showNotes && (
          <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Review Notes
            </label>
            <textarea
              value={notes}
              onChange={(e) => onNotesChange?.(e.target.value)}
              placeholder="Add notes about this review..."
              rows={2}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none"
            />
          </div>
        )}

        {/* Main action bar */}
        <div className="px-4 py-3">
          <div className="flex items-center justify-between gap-4">
            {/* Left: Item metadata */}
            <div className="flex items-center gap-2 text-xs">
              {item && (
                <>
                  {item.priority && (
                    <span className={`px-2 py-0.5 rounded border ${getPriorityBadge(item.priority)}`}>
                      {item.priority.toUpperCase()}
                    </span>
                  )}
                  {item.travel_date && (
                    <span className="text-gray-500">
                      Travel: {item.travel_date}
                    </span>
                  )}
                  {item.created_at && (
                    <span className="text-gray-400">
                      Age: {formatAge(item.created_at)}
                    </span>
                  )}
                </>
              )}
            </div>

            {/* Center: Status indicators */}
            <div className="hidden md:flex items-center gap-2">
              {isDirty && (
                <span className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">
                  Unsaved changes
                </span>
              )}
              {isSaving && (
                <span className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded flex items-center gap-1">
                  <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Saving...
                </span>
              )}
            </div>

            {/* Right: Actions */}
            <div className="flex items-center gap-2">
              {/* Notes toggle */}
              <button
                onClick={() => setShowNotes(!showNotes)}
                className={`p-2 rounded ${showNotes ? 'bg-gray-200 text-gray-700' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}`}
                title="Toggle notes"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </button>

              {/* Keyboard help */}
              <button
                onClick={() => setShowKeyboardHelp(true)}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
                title="Keyboard shortcuts (?)"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </button>

              <div className="w-px h-6 bg-gray-200 mx-1" />

              {/* Assign button */}
              {!isAssignedToMe && (
                <button
                  onClick={onAssign}
                  disabled={isSaving || !item}
                  className="px-3 py-2 text-sm font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Assign to me (A)"
                >
                  Assign
                </button>
              )}

              {/* Reset button */}
              {isDirty && (
                <button
                  onClick={onReset}
                  disabled={isSaving}
                  className="px-3 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Reset changes"
                >
                  Reset
                </button>
              )}

              {/* Delete button */}
              {onDelete && item && (
                showDeleteConfirm ? (
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-red-600">Remove?</span>
                    <button
                      onClick={() => { onDelete(); setShowDeleteConfirm(false); }}
                      disabled={isSaving}
                      className="px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                    >
                      Yes
                    </button>
                    <button
                      onClick={() => setShowDeleteConfirm(false)}
                      className="px-2 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                    >
                      No
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    disabled={isSaving}
                    className="px-3 py-2 text-sm font-medium text-gray-500 hover:text-red-600 hover:bg-red-50 rounded disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                    title="Remove from queue (item is archived)"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    Remove
                  </button>
                )
              )}

              {/* Skip button */}
              <button
                onClick={onSkip}
                disabled={isSaving || currentIndex >= totalItems - 1}
                className="px-3 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                title="Skip to next (S)"
              >
                Skip
              </button>

              {/* Reject button */}
              <button
                onClick={onReject}
                disabled={!canApprove || isSaving}
                className="px-4 py-2 text-sm font-medium text-red-600 hover:text-red-800 hover:bg-red-50 border border-red-200 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                title="Reject (Ctrl+R)"
              >
                Reject
              </button>

              {/* Approve button */}
              <button
                onClick={onApprove}
                disabled={!canApprove || isSaving}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                title="Approve (Ctrl+S)"
              >
                Approve
              </button>

              {/* Approve & Next button */}
              <button
                onClick={onApproveAndNext}
                disabled={!canApprove || isSaving}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                title="Approve & Next (Ctrl+Shift+S)"
              >
                Approve & Next
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Keyboard help modal */}
      {showKeyboardHelp && (
        <KeyboardHelp onClose={() => setShowKeyboardHelp(false)} />
      )}
    </>
  );
};

export default ActionBar;
